import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface FilterCondition {
  metric: string;
  operator: string;
  value: number;
}

interface GrowthTrackingFiltersContextType {
  campaignIds: string[];
  setCampaignIds: (ids: string[]) => void;
  addCampaignId: (id: string) => void;
  removeCampaignId: (id: string) => void;
  clearCampaignIds: () => void;

  adGroupIds: string[];
  setAdGroupIds: (ids: string[]) => void;
  addAdGroupId: (id: string) => void;
  removeAdGroupId: (id: string) => void;
  clearAdGroupIds: () => void;

  adIds: string[];
  setAdIds: (ids: string[]) => void;
  addAdId: (id: string) => void;
  removeAdId: (id: string) => void;
  clearAdIds: () => void;

  targetIds: string[];
  setTargetIds: (ids: string[]) => void;
  addTargetId: (id: string) => void;
  removeTargetId: (id: string) => void;
  clearTargetIds: () => void;

  keywordIds: string[];
  setKeywordIds: (ids: string[]) => void;
  addKeywordId: (id: string) => void;
  removeKeywordId: (id: string) => void;
  clearKeywordIds: () => void;

  placementTypes: string[];
  setPlacementTypes: (ids: string[]) => void;
  addPlacementType: (id: string) => void;
  removePlacementType: (id: string) => void;
  clearPlacementTypes: () => void;

  timeWindowStart: string[];
  setTimeWindowStart: (ids: string[]) => void;
  addTimeWindowStart: (id: string) => void;
  removeTimeWindowStart: (id: string) => void;
  clearTimeWindowStart: () => void;

  metricFilters: FilterCondition[];
  setMetricFilters: (filters: FilterCondition[]) => void;
  clearAllFilters: () => void;
}

const GrowthTrackingFiltersContext = createContext<GrowthTrackingFiltersContextType | undefined>(undefined);

// Helper to create add/remove/clear/setUnique functions for an ID list
function useIdListState(initial: string[] = []) {
  const [ids, setIds] = useState<string[]>(initial);

  const addId = useCallback((id: string) => {
    setIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeId = useCallback((id: string) => {
    setIds(prev => prev.filter(existingId => existingId !== id));
  }, []);

  const clearIds = useCallback(() => {
    setIds([]);
  }, []);

  const setUniqueIds = useCallback((newIds: string[]) => {
    setIds([...new Set(newIds)]);
  }, []);

  return { ids, setUniqueIds, addId, removeId, clearIds };
}

export function GrowthTrackingFiltersProvider({ children }: { children: ReactNode }) {
  const campaign = useIdListState();
  const adGroup = useIdListState();
  const ad = useIdListState();
  const target = useIdListState();
  const keyword = useIdListState();
  const placement = useIdListState();
  const timeWindowStart = useIdListState();
  const [metricFilters, setMetricFilters] = useState<FilterCondition[]>([]);

  const clearAllFilters = useCallback(() => {
    campaign.clearIds();
    adGroup.clearIds();
    ad.clearIds();
    target.clearIds();
    keyword.clearIds();
    placement.clearIds();
    timeWindowStart.clearIds();
    setMetricFilters([]);
  }, [campaign, adGroup, ad, target, keyword, placement, timeWindowStart]);

  return (
    <GrowthTrackingFiltersContext.Provider
      value={{
        campaignIds: campaign.ids,
        setCampaignIds: campaign.setUniqueIds,
        addCampaignId: campaign.addId,
        removeCampaignId: campaign.removeId,
        clearCampaignIds: campaign.clearIds,

        adGroupIds: adGroup.ids,
        setAdGroupIds: adGroup.setUniqueIds,
        addAdGroupId: adGroup.addId,
        removeAdGroupId: adGroup.removeId,
        clearAdGroupIds: adGroup.clearIds,

        adIds: ad.ids,
        setAdIds: ad.setUniqueIds,
        addAdId: ad.addId,
        removeAdId: ad.removeId,
        clearAdIds: ad.clearIds,

        targetIds: target.ids,
        setTargetIds: target.setUniqueIds,
        addTargetId: target.addId,
        removeTargetId: target.removeId,
        clearTargetIds: target.clearIds,

        keywordIds: keyword.ids,
        setKeywordIds: keyword.setUniqueIds,
        addKeywordId: keyword.addId,
        removeKeywordId: keyword.removeId,
        clearKeywordIds: keyword.clearIds,

        placementTypes: placement.ids,
        setPlacementTypes: placement.setUniqueIds,
        addPlacementType: placement.addId,
        removePlacementType: placement.removeId,
        clearPlacementTypes: placement.clearIds,

        timeWindowStart: timeWindowStart.ids,
        setTimeWindowStart: timeWindowStart.setUniqueIds,
        addTimeWindowStart: timeWindowStart.addId,
        removeTimeWindowStart: timeWindowStart.removeId,
        clearTimeWindowStart: timeWindowStart.clearIds,

        metricFilters,
        setMetricFilters,
        clearAllFilters,
      }}
    >
      {children}
    </GrowthTrackingFiltersContext.Provider>
  );
}

export function useGrowthTrackingFilters() {
  const context = useContext(GrowthTrackingFiltersContext);
  if (context === undefined) {
    throw new Error('useGrowthTrackingFilters must be used within a GrowthTrackingFiltersProvider');
  }
  return context;
}

// Keep the old hook name for backward compatibility if needed, but point to the new context
export const useGrowthTrackingCampaignIds = useGrowthTrackingFilters;